import { useId } from 'react';
import type { ChangeEvent } from 'react';

import { getInputControlState, InputControl } from 'ui/forms/input-control';
import {
  TEXT_AREA_DEFAULT_MAX_LENGTH,
  TEXT_AREA_DEFAULT_ROWS,
} from 'ui/text-area/text-area.constants';
import { TextAreaControl } from 'ui/text-area/text-area.styles';
import type { TextAreaProps } from 'ui/text-area/text-area.types';

const TextArea = ({
  label,
  'aria-label': ariaLabel,
  value: rawValue,
  onChange,
  onKeyDown,
  onBlur,
  id,
  name,
  placeholder,
  rows = TEXT_AREA_DEFAULT_ROWS,
  maxLength = TEXT_AREA_DEFAULT_MAX_LENGTH,
  showCharCount = true,
  size = 'large',
  resize = 'vertical',
  disabled,
  required,
  error,
  errorMessage,
  supportiveText,
  helperText,
  ref,
  sx,
  controlSx,
  'data-testid': dataTestId,
}: TextAreaProps) => {
  const value = rawValue ?? '';
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const { isError, describedBy } = getInputControlState({
    id: fieldId,
    disabled,
    error,
    errorMessage,
    supportiveText,
    helperText,
  });
  const counterText =
    showCharCount && !disabled
      ? `${value.length} of ${maxLength} characters used`
      : undefined;
  const inputProps = {
    maxLength,
    'aria-label': ariaLabel,
    'aria-required': required || undefined,
    'aria-invalid': isError || undefined,
    'data-testid': dataTestId,
  };

  const handleChange = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    onChange?.(event.target.value);
  };

  return (
    <InputControl
      id={fieldId}
      size={size}
      label={label}
      required={required}
      disabled={disabled}
      error={error}
      errorMessage={errorMessage}
      supportiveText={supportiveText}
      helperText={helperText}
      counterText={counterText}
      sx={sx}
    >
      <TextAreaControl
        multiline
        fullWidth
        inputComponent="textarea"
        rows={rows}
        size={size}
        id={fieldId}
        name={name}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        inputRef={ref}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        aria-describedby={describedBy}
        textAreaResize={resize}
        sx={controlSx}
        slotProps={{ input: inputProps }}
      />
    </InputControl>
  );
};

export default TextArea;
